<template>
  <content-subpage
    class="acp-html5-interactive-iteration-subpage"
    :visible="visible"
    :titles="titles"
    :content-title="contentTitle"
    :back-label="t('acp_html5_iteration_content_subpage_back_to_generate_label')"
    :has-parent-subpage="hasParentSubpage"
    @content-subpage-hidden="handleCloseSubpage"
    @content-subpage-back="handleBackToGenerate"
  >
    <template #main-content>
      <overlay-loader v-if="isLoading" />
      <template v-else>
        <!-- Description -->
        <div v-safe-html="contentDescription" class="description"></div>

        <div class="content-wrapper">
          <!-- AI Info Notice -->
          <cv-inline-notification kind="info" low-contrast hide-close-button>
            <template #subtitle>
              <div v-safe-html="infoMessage"></div>
            </template>
          </cv-inline-notification>

          <!-- Error Banner -->
          <cv-inline-notification
            v-if="showErrorBanner"
            :kind="
              getErrorBannerInfo(INTEGRATOR_APP_FEATURE_CATEGORY.ACP_HTML5, refinementErrorCode)
                .bannerKind
            "
            :title="
              t(
                getErrorBannerInfo(INTEGRATOR_APP_FEATURE_CATEGORY.ACP_HTML5, refinementErrorCode)
                  .titleKey,
                { feature_name: errorBannerFeatureName },
              )
            "
            low-contrast
            hide-close-button
          >
            <template #subtitle>
              <div>
                {{
                  t(
                    getErrorBannerInfo(
                      INTEGRATOR_APP_FEATURE_CATEGORY.ACP_HTML5,
                      refinementErrorCode,
                    ).subtitleKey,
                    { feature_name: errorBannerFeatureName },
                  )
                }}
              </div>
            </template>
          </cv-inline-notification>

          <cv-inline-notification
            v-if="showMaxDraftsErrorBanner"
            kind="error"
            low-contrast
            hide-close-button
          >
            <template #subtitle>
              <div>
                {{ t("acp_html5_iteration_content_subpage_hit_limit_message") }}
              </div>
            </template>
          </cv-inline-notification>

          <!-- Draft Accordions -->
          <div class="draft-listing">
            <empty-placeholder v-if="showEmptyPlaceholder" class="no-drafts-state">
              <template #icon>
                <GenericEmpty />
              </template>
              <div
                v-safe-html="t('acp_html5_iteration_content_subpage_cannot_generate_message')"
              ></div>
            </empty-placeholder>

            <AccordionWrapper
              v-for="(draft, index) in drafts"
              :key="draft.id"
              kind="gray"
              @state-change="accordionActionChange(index)"
            >
              <cv-accordion-item
                :key="`draft-${draft.id}-${isDraftAccordionExpanded(index)}`"
                :open="isDraftAccordionExpanded(index)"
              >
                <template #title>
                  <div class="draft-title-wrapper" @click.stop>
                    <template v-if="showOriginalInteractiveLabel(draft.type)">
                      <div class="draft-selection">
                        <label class="title">
                          {{ t("acp_html5_iteration_content_subpage_original_interactive_label") }}
                        </label>
                      </div>
                    </template>

                    <template v-else>
                      <!-- Radio button and label -->
                      <div class="draft-selection">
                        <input-radio
                          :id="`draft-${draft.id}`"
                          :value="draft.id"
                          :selected-value="selectedDraftId"
                          kind="white"
                          @change="handleChangeDraft"
                        >
                          <!-- Label -->
                          <label :for="`draft-${draft.id}`" class="title">
                            {{
                              t("acp_html5_iteration_content_subpage_draft_label", {
                                number: getDraftDisplayNumber(draft.id),
                              })
                            }}
                          </label>
                        </input-radio>
                      </div>
                      <!-- Delete button in header -->
                      <span class="delete-icon">
                        <svg-icon
                          name="Trash24"
                          kind="action"
                          @click.stop="deleteDraft(draft.id)"
                        />
                      </span>
                    </template>
                  </div>
                </template>

                <template #content>
                  <div class="accordion-content">
                    <!-- Interactive Preview Frame -->
                    <div class="interactive-preview">
                      <iframe
                        v-if="draft.id"
                        :src="getInteractiveIframeUrl(draft)"
                        class="interactive-frame-iframe"
                        :sandbox="sandboxAttributes"
                      ></iframe>
                    </div>
                    <attachment-placeholder>
                      <template #action-tools>
                        <button
                          type="button"
                          class="open-in-new-tab action"
                          @click="handleOpenInNewTab(draft)"
                        >
                          <svg-icon name="ExternalLink24" kind="action" />
                        </button>
                      </template>
                    </attachment-placeholder>

                    <template v-if="shouldShowRecipesForDraft(draft.id)">
                      <!-- No recipes -->
                      <empty-placeholder v-if="getDraftRecipes(draft.id).length === 0">
                        <template #icon>
                          <GenericEmpty />
                        </template>
                        <div
                          v-safe-html="t('acp_generation_content_subpage_no_recipe_message')"
                        ></div>
                      </empty-placeholder>

                      <cv-form
                        v-if="getDraftRecipes(draft.id).length > 0"
                        class="suggested-roles"
                        @submit.prevent
                      >
                        <!-- Carousel for recipes -->
                        <carousel :start-index="carouselStartIndex">
                          <carousel-item
                            v-for="(recipe, recipeIndex) in getDraftRecipes(draft.id)"
                            :key="recipeIndex"
                          >
                            <recipe-card
                              :id="recipe.id"
                              class="html5-interactive-iteration"
                              icon="Roles32"
                              :title="recipe.title"
                              :content="recipe.content"
                              :is-selected="isDraftRecipeSelected(draft.id, recipe.id)"
                              @on-click="handleRecipeClick(draft.id, $event)"
                            />
                          </carousel-item>
                        </carousel>

                        <!-- Instructions Input -->
                        <dl class="field-set type-text acp-html5-interactive-instruction">
                          <div class="info">
                            {{ t("acp_generation_content_subpage_instructions_message") }}
                          </div>
                          <dd class="field-input">
                            <rich-text-editor
                              :ref="(el) => (instructionsEditorRefs[draft.id] = el)"
                              size="extra_large"
                              kind="plaintext"
                              :placeholder="
                                t('acp_html5_iteration_content_subpage_instruction_placeholder')
                              "
                              :is-mandatory="false"
                              :initial-content="getDraftInstructions(draft.id)"
                              :content="getCurrentlySelectedRecipeContentForDraft(draft.id)"
                              @change="(value) => handleInstructionsInput(draft.id, value)"
                            />
                          </dd>
                        </dl>
                      </cv-form>
                    </template>

                    <div v-if="shouldShowRecipesForDraft(draft.id)" class="regenerate-button">
                      <fetch-data-button
                        kind="ai"
                        class="btn-refine"
                        size="field"
                        :tip-position="refineButton.tipPosition"
                        :tip-alignment="refineButton.tipAlignment"
                        :icon-only="refineButton.iconOnly"
                        :error="refineButton.error"
                        :error-message="refineButton.errorMessage"
                        :loading="isRefineButtonLoading(draft.id)"
                        :completed="refineButton.completed"
                        @click="refineDraft(draft.id, draft.type)"
                        @reset-completed-state="handleRefineButtonResetCompleted"
                        @reset-error-state="handleRefineButtonResetError"
                      >
                        <template v-if="refineButton.error">
                          <span>{{ t("button_state_error") }}</span>
                        </template>
                        <template v-else>
                          <svg-icon name="ACPRegenerate24" kind="action" />
                          {{ t("acp_html5_iteration_content_subpage_refine_button_label") }}
                        </template>
                      </fetch-data-button>
                    </div>

                    <!-- When recipes are hidden (Refine Draft mode) -->
                    <div v-else class="regenerate-button">
                      <cv-button
                        kind="ai"
                        size="field"
                        class="btn-refine-draft"
                        @click="showRecipesForDraft(draft.id)"
                      >
                        <svg-icon name="ACPRegenerate24" kind="action" />
                        {{ t("acp_html5_iteration_content_subpage_refine_draft_button_label") }}
                      </cv-button>
                    </div>
                  </div>
                </template>
              </cv-accordion-item>
            </AccordionWrapper>
          </div>
        </div>
      </template>
    </template>

    <template v-if="showAddButton" #action-button>
      <fetch-data-button
        class="btn-add"
        :tip-position="addButton.tipPosition"
        :tip-alignment="addButton.tipAlignment"
        :icon-only="addButton.iconOnly"
        :disabled="disableAddButton"
        :error="addButton.error"
        :error-message="addButton.errorMessage"
        :loading="addButton.loading"
        :completed="addButton.completed"
        @click="handleAddSelectedDraft"
        @reset-completed-state="handleAddButtonResetCompleted"
        @reset-error-state="handleAddButtonResetError"
      >
        <template v-if="addButton.error">
          <span>{{ t("button_state_error") }}</span>
        </template>
        <template v-else>
          <svg-icon name="Plus24" kind="action" />
          <span>{{ t("acp_html5_iteration_content_subpage_add_button_label") }}</span>
        </template>
      </fetch-data-button>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { onMounted, reactive } from "vue";
import RecipeCard from "@plugins/recipe/components/RecipeCard.vue";
import GenericEmpty from "@images/placeholder/empty-states/GenericEmpty.svg?component";
import {
  ButtonState,
  Html5ListItem,
  AcpHtml5IterationParams,
  RefineButtonState,
  DraftState,
} from "@plugins/authoring-copilot/types.ts";
import {
  ACP_HTML5_APP_FEATURE_TYPE,
  ACP_HTML5_TYPE,
  ACP_REQUEST_STATUS,
  INTEGRATOR_APP_FEATURE_CATEGORY,
} from "@modules/app/service/app.constant.ts";
import appManagementApi from "@apis/app.management.api.ts";
import mediaObjectApi from "@apis/media.object.api.ts";
import appConfigs from "@configs/app.ts";
import authoringCopilotApi from "@apis/authoring.copilot.api.ts";
import richTextUtils from "@plugins/richtext/utils/richtext.utils.ts";
import { Recipe } from "@plugins/recipe/types.ts";
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useRecipe } from "@plugins/recipe/recipe-composable.ts";
import { useProcessingState } from "@modules/app/composables/processing-state.ts";
import { useAcp } from "@plugins/authoring-copilot/authoring-copilot-composable.ts";
import { useApp } from "@plugins/common/composables/app-composable.ts";
import { useErrorBanner } from "@utils/use-error-banner.ts";
import { HTTP_ERROR_CATEGORY } from "@utils/http.error.ts";
import store from "@plugins/store.ts";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import ScrollUtils from "@utils/scroll.utils.ts";
import DOMUtils from "@utils/dom.utils.ts";
import InputRadio from "@plugins/common/components/InputRadio.vue";

interface Props {
  params: AcpHtml5IterationParams;
}

// Props
const props = defineProps<Props>();

const emit = defineEmits<{
  html5MoCreated: [data: unknown];
}>();

const { t } = useI18n();
const { passiveApiFailHandler } = useErrorHandler();
const { getStartIndex, sortRecipesByAlphabeticalOrder, setLastUsedRecipeToLocalStorage } =
  useRecipe();
const processingId = ref("acp-html5-interactive-iteration");
const { resetProcessing, setProcessing, isProcessing } = useProcessingState(processingId.value);
const {
  visible,
  titles,
  actionHidden,
  pollForStatus,
  actionBack,
  hasParentSubpage,
  defaultExpose,
} = useAcp({
  contentSubpageParams: props.params,
  waitModalProcessingTitle: t("acp_html5_generation_wait_modal_title"),
  waitModalProcessingContent: t("acp_html5_generation_wait_modal_content"),
});
const modal = useModal();
const { isAcpHtml5AppAvailable, getAppFeatureName } = useApp();
const { getErrorBannerInfo } = useErrorBanner();
const errorBannerFeatureName = computed(() =>
  getAppFeatureName(INTEGRATOR_APP_FEATURE_CATEGORY.ACP_HTML5),
);
const instructionsEditorRefs = reactive({});
defineExpose(defaultExpose);

// Data
const isLoading = ref(false);
const drafts = ref<Html5ListItem[]>([]);
const selectedDraftId = ref<string | null>(null);
const draftStates = ref<Record<string, DraftState>>({});
const recipes = ref<Recipe[]>([]);
const activeDraftWithRecipes = ref<string | null>(null);
const maxInstructionLength = ref(30000);
const requestId = ref("");
const maxDrafts = ref(5);
const sandboxAttributes = ref("allow-scripts allow-same-origin allow-forms");
const nextDraftNumber = ref(1);
const draftNumbers = ref<Record<string, number>>({});
const showErrorBanner = ref(false);
const refinementErrorCode = ref<string | undefined>(undefined);
const showMaxDraftsErrorBanner = ref(false);
const showEmptyPlaceholder = ref(false);
const addButton = ref<ButtonState>({
  error: false,
  errorMessage: "",
  loading: false,
  completed: false,
  iconOnly: false,
  tipPosition: "top",
  tipAlignment: "end",
});
const refineButton = ref<RefineButtonState>({
  error: false,
  errorMessage: "",
  loading: false,
  completed: false,
  iconOnly: false,
  tipPosition: "top",
  tipAlignment: "end",
  targetDraftId: null,
});
const carouselStartIndex = ref(0);

// Computed
const userId = computed(() => store.getters["auth/userId"]);

const showAddButton = computed(() => {
  return !isLoading.value;
});

const disableAddButton = computed(() => {
  return (
    isProcessing.value ||
    !selectedDraftId.value ||
    drafts.value.length === 0 ||
    refineButton.value.loading ||
    (drafts.value.length === 1 && drafts.value[0].type === ACP_HTML5_TYPE.MO)
  );
});

const appFeatureId = computed(() => {
  const appFeatures = store.getters["app/getIntegratorAppAccessByCategory"](
    INTEGRATOR_APP_FEATURE_CATEGORY.ACP_HTML5,
  );
  const acpHtml5GenerateAppFeature = appFeatures.find(
    (appFeature) => appFeature.featureType === ACP_HTML5_APP_FEATURE_TYPE.ACP_HTML5_RE_ANTHROPIC,
  );
  return acpHtml5GenerateAppFeature ? parseInt(acpHtml5GenerateAppFeature.appFeatureId) : null;
});

const richTextContext = computed(() => {
  return props.params.richTextContext;
});

const contentTitle = computed(() => {
  if (drafts.value.some((draft) => draft.type === ACP_HTML5_TYPE.MO)) {
    return t("acp_html5_refine_content_subpage_title");
  }
  return t("acp_html5_iteration_content_subpage_title");
});

const contentDescription = computed(() => {
  if (drafts.value.some((draft) => draft.type === ACP_HTML5_TYPE.MO)) {
    return t("acp_html5_refine_content_subpage_description", {
      copyrightWorksUrl: appConfigs.copyrightResourcesUrl,
      acpHtml5InteractiveGuideUrl: appConfigs.acpHtml5InteractiveGuideUrl,
    });
  }
  return t("acp_html5_iteration_content_subpage_description");
});

const infoMessage = computed(() => {
  return t("acp_html5_iteration_content_subpage_info_message", {
    copyrightWorksUrl: appConfigs.copyrightResourcesUrl,
  });
});

// Methods
const init = function () {
  drafts.value =
    props.params.html5List && props.params.html5List.length > 0 ? props.params.html5List : [];

  if (drafts.value.length === 1 && drafts.value[0].type === ACP_HTML5_TYPE.MO) {
    maxDrafts.value += 1;
  }

  titles.value.push(contentTitle.value);

  // Initialize draft states
  drafts.value.forEach((draft, index) => {
    draftNumbers.value[draft.id] = drafts.value.length - index;

    draftStates.value[draft.id] = {
      expanded: index === 0,
      selectedRecipeId: null,
      instructions: "",
      hasUpdatedInstructionValue: false,
    };
  });

  nextDraftNumber.value = drafts.value.length + 1;

  if (drafts.value.length > 0) {
    selectedDraftId.value = drafts.value[0].id;
    requestId.value = selectedDraftId.value;
    activeDraftWithRecipes.value = drafts.value[0].id;
  }
};

const fetchRecipes = async function () {
  if (!isAcpHtml5AppAvailable.value) {
    return;
  }

  try {
    const response = await fetchRecipesData(appFeatureId.value);
    if (response.code !== "ok") {
      throw new Error("Failed to fetch recipes");
    }
    recipes.value = sortRecipesByAlphabeticalOrder(response.data);
    carouselStartIndex.value = getStartIndex(recipes.value);
  } catch (error) {
    passiveApiFailHandler(error);
    throw error;
  }
};

const showOriginalInteractiveLabel = function (draftType: string) {
  return draftType === ACP_HTML5_TYPE.MO;
};

const getInteractiveIframeUrl = function (draft: Html5ListItem) {
  if (draft.type === ACP_HTML5_TYPE.MO) {
    return draft.fileUrl;
  }
  return `${appConfigs.s3ResourceDomain}/authoring-copilot/html5/${draft.id}/index.html`;
};

const handleOpenInNewTab = function (draft: Html5ListItem) {
  window.open(getInteractiveIframeUrl(draft), "_blank");
};

const fetchRecipesData = function (featureRecipeId: number | null) {
  return new Promise((resolve, reject) => {
    if (!featureRecipeId) {
      reject(new Error("Feature recipe ID is null"));
      return;
    }
    appManagementApi.getIntegratorAppFeatureRecipe(featureRecipeId).then(
      (response) => {
        if (response.code !== "ok") {
          reject(new Error("API returned non-ok status"));
          return;
        }
        resolve(response);
      },
      (error) => {
        reject(error);
      },
    );
  });
};

const accordionActionChange = function (index: number) {
  const draft = drafts.value[index];
  if (draft && draftStates.value[draft.id]) {
    draftStates.value[draft.id].expanded = !draftStates.value[draft.id].expanded;
  }
};

const isDraftAccordionExpanded = function (index: number): boolean {
  const draft = drafts.value[index];
  return draft ? draftStates.value[draft.id]?.expanded || false : false;
};

const shouldShowRecipesForDraft = function (draftId: string): boolean {
  return activeDraftWithRecipes.value === draftId;
};

const getDraftRecipes = function (draftId: string): Recipe[] {
  return shouldShowRecipesForDraft(draftId) ? recipes.value : [];
};

const isDraftRecipeSelected = function (draftId: string, recipeId: number): boolean {
  return draftStates.value[draftId]?.selectedRecipeId === recipeId;
};

const isRefineButtonLoading = function (draftId: string): boolean {
  return refineButton.value.loading && refineButton.value.targetDraftId === draftId;
};

const getDraftDisplayNumber = function (draftId: string): number {
  const hasMO = drafts.value.some((d) => d.type === ACP_HTML5_TYPE.MO);
  return hasMO ? draftNumbers.value[draftId] - 1 || 0 : draftNumbers.value[draftId] || 0;
};

const getDraftInstructions = function (draftId: string): string {
  return draftStates.value[draftId]?.instructions || "";
};

const getCurrentlySelectedRecipeContentForDraft = function (draftId: string): string {
  const draftState = draftStates.value[draftId];
  if (!draftState) {
    return "";
  }

  const selectedRecipeId = draftState.selectedRecipeId;
  if (!selectedRecipeId) {
    if (!draftState.hasUpdatedInstructionValue) {
      return "";
    }
    draftState.hasUpdatedInstructionValue = false;
    return draftState.instructions;
  }

  draftState.hasUpdatedInstructionValue = false;
  return recipes.value.find((r) => r.id === selectedRecipeId)?.content || "";
};

const handleInstructionsInput = function (draftId: string, value: string) {
  const editor = instructionsEditorRefs[draftId];
  if (editor) {
    editor.clearError();
  }
  if (draftStates.value[draftId]) {
    draftStates.value[draftId].instructions = value;
    if (draftStates.value[draftId].selectedRecipeId) {
      draftStates.value[draftId].hasUpdatedInstructionValue = true;
    }
  }
};

const handleRecipeClick = function (draftId: string, event: { id: number }) {
  if (!draftStates.value[draftId]) return;

  const editor = instructionsEditorRefs[draftId];
  if (editor) {
    editor.clearError();
  }

  const draftState = draftStates.value[draftId];

  if (draftState.selectedRecipeId === event.id) {
    // Unselect if already selected
    if (!draftState.hasUpdatedInstructionValue) {
      resetInstructionInputValue(draftId);
    }
    draftState.selectedRecipeId = null;
  } else {
    draftState.selectedRecipeId = event.id;
    updateInstructionInputValue(draftId, event.id);
  }
};

const updateInstructionInputValue = function (draftId: string, recipeId: number) {
  const recipe = recipes.value.find((r) => r.id === recipeId);
  if (recipe && draftStates.value[draftId]) {
    draftStates.value[draftId].instructions = recipe.content;
  }
};

const resetInstructionInputValue = function (draftId: string) {
  if (draftStates.value[draftId]) {
    draftStates.value[draftId].instructions = "";
  }
};

const handleAddSelectedDraft = async function () {
  if (!selectedDraftId.value) return;
  requestId.value = selectedDraftId.value;
  addButton.value.loading = true;
  setProcessing(processingId.value);

  try {
    const fileName = `${requestId.value}.zip`;
    const html5MoModel = {
      contextUuid: richTextContext.value.resourceUuid,
      contextType: richTextContext.value.resourceType,
      uploadFrom: richTextContext.value.uploadFrom,
      fileName: fileName,
      resourceDomain: richTextContext.value.resourceDomain,
      ownerGroupId: richTextContext.value.ownerGroupId,
      requestId: requestId.value,
    };
    const response = await mediaObjectApi.createHtml5MediaObject({ ...html5MoModel });
    const responseData = response?.data;
    emit("html5MoCreated", {
      fileType: responseData?.fileType,
      uuid: responseData?.uuid,
      fileName: fileName,
      type: "mo",
    });

    handleAddSuccess();
  } catch (error) {
    passiveApiFailHandler(error);
    handleAddError();
  }
};

const handleChangeDraft = function (value: string) {
  selectedDraftId.value = value;
};

const showRecipesForDraft = function (draftId: string) {
  activeDraftWithRecipes.value = draftId;
};

const refineDraft = async function (draftId: string, draftType: string) {
  showEmptyPlaceholder.value = false;
  showMaxDraftsErrorBanner.value = false;
  showErrorBanner.value = false;
  refinementErrorCode.value = undefined;
  // Validate iteration limit first
  if (drafts.value.length >= maxDrafts.value) {
    showMaxDraftsErrorBanner.value = true;
    return;
  }

  // Validate that user has access/permission to ACP HTML5 app
  if (!isAcpHtml5AppAvailable.value) {
    return;
  }

  // Validate instructions length
  const instructions = draftStates.value[draftId]?.instructions || "";
  const cleanedInstructions = richTextUtils.removeMediaAndStripHTML(instructions);
  if (cleanedInstructions.length > maxInstructionLength.value) {
    refineButton.value.error = true;
    return;
  }

  refineButton.value.loading = true;
  refineButton.value.targetDraftId = draftId;

  try {
    // Prepare request data for refinement
    const requestData = getRefineInteractiveRequestData(draftId, draftType);
    activeDraftWithRecipes.value = draftId;

    const draftState = draftStates.value[draftId];

    setLastUsedRecipeToLocalStorage(userId.value, draftState.selectedRecipeId);

    const response = await authoringCopilotApi.generateAcpHtml5Interactive(requestData);

    if (response.code !== "ok" || !response.data?.id) {
      throw new Error("Failed to refine interactive");
    }

    // Store request ID for polling
    requestId.value = response.data.id;

    try {
      const pollResult = await pollForStatus(
        requestId.value,
        authoringCopilotApi.getAcpHtml5InteractiveStatus,
      );
      handleRefinementComplete(pollResult);
    } catch (error) {
      handleRefinementError();
      passiveApiFailHandler(error);
    }
  } catch (error) {
    handleRefinementError();
    passiveApiFailHandler(error);
  }
};

const getRefineInteractiveRequestData = function (draftId: string, draftType: string) {
  const instructions = draftStates.value[draftId]?.instructions || "";
  if (draftType === ACP_HTML5_TYPE.MO) {
    return {
      mediaObjectUuid: draftId,
      rt_instruction: instructions.trim(),
    };
  }
  return {
    previousHtml5DraftRequestId: draftId,
    rt_instruction: instructions.trim(),
  };
};

const handleRefinementComplete = function (pollResult: unknown) {
  const status = pollResult?.data?.status;

  if (status === ACP_REQUEST_STATUS.SUCCESS) {
    // Create new draft with refined content
    const newHtml5ListItem: Html5ListItem = {
      id: requestId.value,
      type: ACP_HTML5_TYPE.DRAFT,
    };

    drafts.value.unshift(newHtml5ListItem);

    draftNumbers.value[requestId.value] = nextDraftNumber.value;
    nextDraftNumber.value++;

    // Initialize state for new draft
    draftStates.value[newHtml5ListItem.id] = {
      expanded: true,
      selectedRecipeId: null,
      instructions: "",
      hasUpdatedInstructionValue: false,
    };

    // Set new draft as selected
    selectedDraftId.value = newHtml5ListItem.id;

    handleRefinementSuccess();
  } else if (status === ACP_REQUEST_STATUS.NO_RESULT) {
    // NO_RESULT means the LLM replied but the payload failed SLS validation,
    // so the lambda does not send an errorCode for it
    handleRefinementError(HTTP_ERROR_CATEGORY.INVALID_RESPONSE);
  } else {
    handleRefinementError(pollResult?.data?.errorCode);
  }
};

const handleRefinementSuccess = function () {
  refineButton.value.loading = false;
  refineButton.value.targetDraftId = null;

  ScrollUtils.scrollToTopOfContainer(DOMUtils.el(".acp-html5-interactive-iteration-subpage .main"));
};

const handleRefinementError = function (errorCode?: string) {
  refineButton.value.loading = false;
  refineButton.value.targetDraftId = null;
  refineButton.value.error = true;
  refinementErrorCode.value = errorCode;
  showErrorBanner.value = true;
};

const deleteDraft = function (draftId: string) {
  modal.showMessage("warning", {
    title: t("acp_html5_iteration_content_subpage_delete_draft_title"),
    content: t("acp_html5_iteration_content_subpage_delete_draft_subtitle"),
    primaryBtnLabel: t("button_delete"),
    secondaryBtnLabel: t("button_cancel"),
    actionPrimary: () => {
      performDraftDeletion(draftId);
    },
  });
};

const performDraftDeletion = function (draftId: string) {
  drafts.value = drafts.value.filter((draft) => draft.id !== draftId);

  delete draftStates.value[draftId];

  // If the deleted draft was the one with recipes, move recipes to the latest remaining draft
  if (activeDraftWithRecipes.value === draftId && drafts.value.length > 0) {
    activeDraftWithRecipes.value = drafts.value[drafts.value.length - 1].id;
  } else if (drafts.value.length === 0) {
    activeDraftWithRecipes.value = null;
    showEmptyPlaceholder.value = true;
  }

  if (selectedDraftId.value === draftId) {
    if (drafts.value.length > 0) {
      selectedDraftId.value = drafts.value[0].id;
    } else {
      selectedDraftId.value = null;
    }
  }

  showMaxDraftsErrorBanner.value = false;
};

const handleBackToGenerate = function () {
  modal.showMessage("info", {
    title: t("acp_html5_interactive_iteration_back_confirmation_title"),
    content: t("acp_html5_interactive_iteration_back_confirmation_message"),
    primaryBtnLabel: t("button_proceed"),
    secondaryBtnLabel: t("button_cancel"),
    actionPrimary: () => {
      actionBack();
    },
  });
};

const handleAddSuccess = function () {
  resetProcessing();
  addButton.value.completed = true;
  addButton.value.loading = false;
  actionHidden();
};

const handleAddError = function () {
  addButton.value.loading = false;
  addButton.value.error = true;
  resetProcessing();
};

const handleAddButtonResetCompleted = function () {
  addButton.value.completed = false;
};

const handleAddButtonResetError = function () {
  addButton.value.error = false;
  addButton.value.errorMessage = "";
};

const handleCloseSubpage = function () {
  actionHidden();
};

const handleRefineButtonResetCompleted = function () {
  refineButton.value.completed = false;
};

const handleRefineButtonResetError = function () {
  refineButton.value.error = false;
  refineButton.value.errorMessage = "";
};

onMounted(async () => {
  isLoading.value = true;

  try {
    await fetchRecipes();
    init();
  } catch (error) {
    passiveApiFailHandler(error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<style lang="scss">
@import "@styles/abstracts/variables";
@import "@styles/layout/breakpoint";
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.acp-html5-interactive-iteration-subpage {
  &.bx--modal {
    .bx--modal-container {
      > .content {
        > .main {
          padding-bottom: 0;
        }
      }
    }
  }

  .description {
    @include carbon--type-style("body-long-01", true);
    margin-bottom: $spacing-05;
  }

  .content-wrapper {
    background-color: $ui-01;
    margin-left: -$spacing-05;
    margin-right: -$spacing-05;
    padding: $spacing-01 $spacing-05 $spacing-05 $spacing-05;
  }

  .draft-listing {
    > .empty-placeholder {
      &.no-drafts-state {
        margin: $spacing-05 0;
        padding: $spacing-09 0 3.5rem;
        border: 1px solid $ui-03;
        border-radius: 10px;

        .main {
          > div {
            max-width: $comfort-reading-width;
          }
        }
      }

      .main {
        > div {
          max-width: calc(100% - 26rem);
        }
      }
    }

    .bx--accordion {
      position: relative;
      margin-bottom: $spacing-05;

      &__heading {
        padding: $spacing-04 0 $spacing-04 $spacing-05;

        .bx--accordion__title {
          margin-left: $spacing-01;
          padding-right: $spacing-11;
          display: flex;
          align-items: center;
          gap: $spacing-03;

          &::after {
            top: 5px;
            right: 1.2rem;
          }

          input[type="radio"] {
            margin: 0;
          }

          .title {
            @include carbon--type-style("productive-heading-01", true);
            cursor: pointer;
          }

          .delete-icon {
            position: absolute;
            display: inline-block;
            right: 2.75rem;
            top: 0rem;
          }
        }
      }

      &__item {
        margin-bottom: $spacing-05;
      }

      &__item--active {
        .bx--accordion__heading {
          .bx--accordion__title {
            &::after {
              top: 9px;
            }
          }
        }

        & ~ .menu-actions {
          .bx--overflow-menu__trigger {
            .sls-icon {
              [fill] {
                fill: $inverse-link-01;
              }
            }
          }
        }
      }

      > .menu-actions {
        position: absolute;
        top: 0.25rem;
        right: 0.3rem;
        z-index: z-map("default");
      }

      &__content {
        padding: $spacing-05;
        border: 1px solid $ui-03;
      }
    }

    .bx--accordion__item--active {
      .bx--accordion__title {
        .delete-icon {
          .sls-icon {
            [stroke] {
              stroke: #abc2ee;
            }
          }
        }
      }
    }

    .draft-selection {
      display: flex;
      align-items: center;
      gap: $spacing-03;

      .input-radio {
        margin: 0;

        > label {
          align-items: center;

          .input-icon {
            .sls-icon {
              &[name="RadioChecked24"] {
                [stroke] {
                  stroke: $interactive-01;
                }
              }
            }
          }

          .text {
            margin-top: 0;
          }
        }
      }
    }

    .accordion-content {
      .interactive-preview {
        position: relative;
        display: flex;
        max-height: 480px;
        overflow: auto;

        iframe {
          width: 100%;
          min-height: 480px;
          border: 1px solid $ui-02;
          border-radius: 10px;
        }
      }

      .attachment-placeholder {
        margin-bottom: $spacing-05;
        background-color: $ui-background;
      }

      // Updated styles to match generation subpage
      .suggested-roles {
        margin-bottom: $spacing-05;

        .info {
          @include carbon--type-style("body-short-01");
          margin-bottom: $spacing-03;
          color: $text-02;
        }

        .mce-content-body {
          background-color: $ui-background;
        }

        .empty-placeholder {
          margin: $spacing-06 0 $spacing-05;

          .content {
            margin-top: 0;
          }
        }

        .field-label {
          @include carbon--type-style("body-short-01");
          color: $text-02;
        }

        .field-value {
          @include carbon--type-style("helper-text-01");
          color: $text-05;
        }

        .action {
          display: flex;
          justify-content: center;
        }
      }

      .instructions-section {
        margin-bottom: $spacing-05;

        .section-title {
          @include carbon--type-style("productive-heading-02");
          color: $text-01;
          margin-bottom: $spacing-03;
        }

        .instructions-content {
          @include carbon--type-style("body-long-01");
          color: $text-02;
          line-height: 1.5;
          padding: $spacing-04;
          background-color: $ui-02;
          border-radius: 4px;
        }
      }

      .regenerate-button {
        display: flex;
        justify-content: center;
      }
    }

    .draft-title-wrapper {
      display: flex;
      gap: $spacing-03;
      align-items: center;
    }
  }
}
</style>
